import { useState } from 'react';
import { Award, Lightbulb, X } from 'lucide-react';
import { FileUpload, RichTextEditor, type UploadFile } from '@ampersandbrands/ui';
import { HtmlBody } from '@/components/HtmlBody';
import { usePublishedContent, useSubmitContent, type ContentItem, type SubmissionBody } from '@/lib/useContent';
import { useMarkAreaSeen } from '@/lib/useHomeSummary';

function ContentCard({ item }: { item: ContentItem }) {
  return (
    <div className="rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <h3 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">{item.title}</h3>
        {item.store_no && (
          <span className="mt-0.5 shrink-0 rounded-pill bg-candy-cream-warm px-2 py-0.5 text-[11px] font-medium text-candy-ink-muted dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink-muted">
            Store {item.store_no}
          </span>
        )}
      </div>
      {item.body_html && <HtmlBody src={item.body_html} />}
    </div>
  );
}

type FormArea = 'recognition' | 'ideas';

export function Recognition() {
  useMarkAreaSeen('recognition');
  const [tab, setTab] = useState<'recognition' | 'ideas'>('recognition');
  const [formArea, setFormArea] = useState<FormArea | null>(null);
  const [title, setTitle] = useState('');
  // The story is kept twice: the HTML that gets stored and rendered, and the
  // plain text, which is only used to tell an empty editor from a written one.
  // A cleared editor still holds an empty paragraph, so the HTML is never "".
  const [storyHtml, setStoryHtml] = useState('');
  const [storyText, setStoryText] = useState('');
  const [photos, setPhotos] = useState<UploadFile[]>([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const photo = photos[0]?.file ?? null;

  const recQ = usePublishedContent('recognition');
  const ideasQ = usePublishedContent('ideas');
  const submitMut = useSubmitContent();

  function openForm(area: FormArea) {
    setFormArea(area);
    setTitle('');
    setStoryHtml('');
    setStoryText('');
    setPhotos([]);
    setSubmitSuccess(false);
    submitMut.reset();
  }

  function closeForm() {
    setFormArea(null);
    setSubmitSuccess(false);
    submitMut.reset();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formArea || !title.trim()) return;

    const body: SubmissionBody = {
      area: formArea,
      title: title.trim(),
      ...(storyText.trim() ? { body_html: storyHtml } : {}),
      ...(photo ? { attachment: { filename: photo.name, content_type: photo.type } } : {}),
    };

    await submitMut.mutateAsync({ body, file: photo ?? undefined });
    setSubmitSuccess(true);
  }

  const isLoading = recQ.isLoading || ideasQ.isLoading;
  const activeItems = (tab === 'recognition' ? recQ.data : ideasQ.data) ?? [];

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Recognition</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">Celebrate wins &amp; share ideas</p>
      </div>

      {/* Action buttons — hidden while form is open */}
      {!formArea && (
        <div className="mb-5 flex gap-3">
          <button
            type="button"
            onClick={() => openForm('recognition')}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-candy-pink px-4 py-3 text-[14px] font-semibold text-white shadow-1 hover:opacity-90"
          >
            <Award className="h-4 w-4" aria-hidden /> Share a win
          </button>
          <button
            type="button"
            onClick={() => openForm('ideas')}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-candy-line bg-candy-white px-4 py-3 text-[14px] font-semibold text-candy-ink shadow-1 hover:bg-candy-cream-warm dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:hover:bg-candy-dark-surface-warm"
          >
            <Lightbulb className="h-4 w-4" aria-hidden /> Suggest an idea
          </button>
        </div>
      )}

      {/* Inline submission form */}
      {formArea && (
        <div className="mb-5 rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">
              {formArea === 'recognition' ? 'Share a win' : 'Suggest an idea'}
            </h2>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-full p-1 text-candy-ink-muted hover:bg-candy-cream-warm dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
              aria-label="Close form"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {submitSuccess ? (
            <div className="py-2 text-center">
              <p className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">Submitted!</p>
              <p className="mt-1 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
                HQ reviews submissions before they appear here. Thanks for sharing!
              </p>
              <button
                type="button"
                onClick={closeForm}
                className="mt-4 rounded-pill bg-candy-cream-warm px-4 py-2 text-sm font-medium text-candy-ink hover:bg-candy-line dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder={formArea === 'recognition' ? 'What happened?' : 'Your idea in a few words'}
                  className="w-full rounded-lg border border-candy-line bg-candy-cream px-3 py-2 text-sm text-candy-ink placeholder:text-candy-ink-muted focus:outline-none focus:ring-2 focus:ring-candy-pink dark:border-candy-dark-line dark:bg-candy-dark-bg dark:text-candy-dark-ink dark:placeholder:text-candy-dark-ink-muted dark:focus:ring-candy-pink-dark"
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  Story
                </label>
                <RichTextEditor
                  value={storyHtml}
                  onChange={(html, text) => {
                    setStoryHtml(html);
                    setStoryText(text);
                  }}
                  aria-label={formArea === 'recognition' ? 'Story' : 'Your idea'}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  Photo (optional)
                </label>
                {/* One photo, not several. A submission carries exactly one
                    attachment on the api side (it hands back a single upload
                    link), so offering a second picker slot would take a file
                    and then quietly drop it. The shared picker still brings
                    drag-and-drop, the camera, and real error messages. */}
                <FileUpload
                  files={photos}
                  onChange={setPhotos}
                  multiple={false}
                  maxFiles={1}
                  accept={['image/*']}
                  capture="environment"
                  label="Add a photo"
                />
              </div>
              {submitMut.error && (
                <p className="text-[12px] text-red-500">Could not submit. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={!title.trim() || submitMut.isPending}
                className="w-full rounded-pill bg-candy-pink py-2.5 text-[14px] font-semibold text-white shadow-1 hover:opacity-90 disabled:opacity-50 dark:bg-candy-pink-dark"
              >
                {submitMut.isPending ? 'Submitting…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Tab switcher */}
      <div className="mb-4 flex gap-1.5">
        {(['recognition', 'ideas'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-pill px-3 py-1.5 text-[13px] font-medium capitalize transition-colors ${
              tab === t
                ? 'bg-candy-pink text-white dark:bg-candy-pink-dark dark:text-candy-dark-bg'
                : 'bg-candy-cream-warm text-candy-ink-muted hover:text-candy-ink dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Loading skeletons */}
      {isLoading && (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activeItems.length === 0 && (
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Nothing here yet — HQ content is coming.
          </p>
        </div>
      )}

      {/* Card wall */}
      {!isLoading && activeItems.length > 0 && (
        <div className="space-y-4">
          {activeItems.map((item) => (
            <ContentCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
